import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { posAPI } from '@/services/api';
import { ArrowLeftRight, Minus, Plus, IndianRupee, Banknote, CreditCard, Wallet, CheckCircle, Printer, AlertCircle, Search, Package, X, ArrowRight } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';

const ExchangeDialog = ({ open, onOpenChange, bill, storeId, onSuccess }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1); // 1: Select Return Items, 2: Add New Items, 3: Settlement
  const [returnItems, setReturnItems] = useState([]);
  const [newItems, setNewItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [exchangeComplete, setExchangeComplete] = useState(false);
  const [exchangeResult, setExchangeResult] = useState(null);
  const receiptRef = useRef(null);

  // Initialize return items from bill
  useEffect(() => {
    if (bill && open) {
      const items = bill.items?.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        sku: item.sku,
        quantity: item.quantity,
        returned_qty: item.returned_qty || 0,
        exchanged_qty: item.exchanged_qty || 0,
        available_qty: item.quantity - (item.returned_qty || 0) - (item.exchanged_qty || 0),
        return_qty: 0,
        unit_price: item.unit_price,
        tax_rate: item.tax_rate || 0,
        reason: 'exchange'
      })) || [];
      setReturnItems(items);
      setNewItems([]);
      setStep(1);
      setExchangeComplete(false);
      setExchangeResult(null);
      setNotes('');
      setPaymentMode('cash');
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [bill, open]);

  // Calculate totals
  const calculateTotals = () => {
    let returnTotal = 0;
    let newTotal = 0;
    
    returnItems.forEach(item => {
      if (item.return_qty > 0) {
        returnTotal += item.return_qty * item.unit_price;
      }
    });
    
    newItems.forEach(item => {
      newTotal += item.quantity * item.unit_price;
    });
    
    const difference = newTotal - returnTotal;
    
    return {
      returnTotal: Math.round(returnTotal * 100) / 100,
      newTotal: Math.round(newTotal * 100) / 100,
      difference: Math.round(difference * 100) / 100
    };
  };

  const localTotals = calculateTotals();
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState('');
  const [quoting, setQuoting] = useState(false);
  const quoteKey = JSON.stringify([returnItems.map(i => [i.product_id, i.return_qty]), newItems.map(i => [i.product_id, i.quantity]), paymentMode]);
  // Server quote: refund at the price actually paid, best live offer on new items, exact balance
  useEffect(() => {
    if (!open || !bill?.id || exchangeComplete) return;
    const rets = returnItems.filter(i => i.return_qty > 0);
    if (!rets.length && !newItems.length) { setQuote(null); setQuoteError(''); return; }
    let alive = true;
    setQuoting(true);
    posAPI.quoteExchange({
      sale_id: bill.id,
      items: rets.map(i => ({ return_product_id: i.product_id, return_quantity: i.return_qty, return_reason: i.reason || 'exchange' })),
      new_items: newItems.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
      payment_mode: paymentMode,
    }).then(r => { if (alive) { setQuote(r.data); setQuoteError(''); } })
      .catch(e => { if (alive) { setQuote(null); setQuoteError(e.response?.data?.detail || 'Could not price the exchange'); } })
      .finally(() => { if (alive) setQuoting(false); });
    return () => { alive = false; };
  }, [quoteKey, open, bill?.id, exchangeComplete]); // eslint-disable-line react-hooks/exhaustive-deps
  const totals = quote ? { returnTotal: quote.total_return_value, newTotal: quote.total_new_value, difference: quote.price_difference } : localTotals;
  const quotedNew = (pid) => quote?.new_lines?.find(l => l.product_id === pid);
  const quotedRet = (pid) => quote?.return_lines?.find(l => l.product_id === pid);
  const newLineValue = (item) => { const q = quotedNew(item.product_id); return q ? q.value : item.quantity * item.unit_price; };
  const retLineValue = (item) => { const q = quotedRet(item.product_id); return q ? q.value : item.return_qty * item.unit_price; };

  // Update return quantity
  const updateReturnQty = (index, qty) => {
    const item = returnItems[index];
    const newQty = Math.max(0, Math.min(qty, item.available_qty));
    setReturnItems(prev => prev.map((it, i) => 
      i === index ? { ...it, return_qty: newQty } : it
    ));
  };

  // Update reason
  const updateReason = (index, reason) => {
    setReturnItems(prev => prev.map((it, i) => 
      i === index ? { ...it, reason } : it
    ));
  };

  // Search products
  const searchProducts = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await posAPI.searchProducts(searchQuery, storeId);
      setSearchResults(res.data || []);
    } catch (error) {
      console.error('Search failed:', error);
      toast({ title: 'Error', description: 'Failed to search products', variant: 'destructive' });
    } finally {
      setSearching(false);
    }
  };

  // Add new item
  const addNewItem = (product) => {
    const existing = newItems.find(it => it.product_id === product.id);
    if (existing) {
      setNewItems(prev => prev.map(it => 
        it.product_id === product.id 
          ? { ...it, quantity: it.quantity + 1 }
          : it
      ));
    } else {
      setNewItems(prev => [...prev, {
        product_id: product.id,
        product_name: product.name,
        sku: product.sku,
        quantity: 1,
        unit_price: product.current_price?.selling_price || product.selling_price || 0,
        tax_rate: product.tax_rate || 0
      }]);
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  // Update new item quantity
  const updateNewItemQty = (index, qty) => {
    if (qty <= 0) {
      setNewItems(prev => prev.filter((_, i) => i !== index));
    } else {
      setNewItems(prev => prev.map((it, i) => 
        i === index ? { ...it, quantity: qty } : it
      ));
    }
  };

  // Remove new item
  const removeNewItem = (index) => {
    setNewItems(prev => prev.filter((_, i) => i !== index));
  };

  // Proceed to next step
  const proceedToStep2 = () => {
    const itemsToReturn = returnItems.filter(it => it.return_qty > 0);
    if (itemsToReturn.length === 0) {
      toast({ title: 'Error', description: 'Select at least one item to exchange', variant: 'destructive' });
      return;
    }
    setStep(2);
  };

  const proceedToStep3 = () => {
    if (newItems.length === 0) {
      toast({ title: 'Error', description: 'Add at least one new item for exchange', variant: 'destructive' });
      return;
    }
    setStep(3);
  };

  // Process the exchange
  const processExchange = async () => {
    setProcessing(true);
    try {
      const exchangeItems = returnItems
        .filter(it => it.return_qty > 0)
        .map((returnItem, idx) => ({
          return_product_id: returnItem.product_id,
          return_quantity: returnItem.return_qty,
          return_reason: returnItem.reason,
          new_product_id: newItems[idx % newItems.length]?.product_id || newItems[0]?.product_id,
          new_quantity: idx === 0 ? newItems.reduce((sum, it) => sum + it.quantity, 0) : 0
        }));

      // Every returned line + every new line is sent explicitly (no pairing, nothing dropped)
      const res = await posAPI.createExchange({
        sale_id: bill.id,
        items: returnItems.filter(it => it.return_qty > 0).map(it => ({
          return_product_id: it.product_id, return_quantity: it.return_qty, return_reason: it.reason || 'exchange', new_product_id: null, new_quantity: 0,
        })),
        new_items: newItems.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        payment_mode: totals.difference !== 0 ? paymentMode : null, // Send payment mode for both collection and refund
        payment_amount: Math.abs(totals.difference), // Always send absolute amount
        notes: notes
      });

      setExchangeResult(res.data);
      setExchangeComplete(true);
      toast({ title: 'Success', description: 'Exchange processed successfully' });
      
    } catch (error) {
      console.error('Exchange failed:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to process exchange', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  // Print receipt
  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `Exchange-${exchangeResult?.exchange_number || 'Receipt'}`,
  });

  // Close and refresh
  const handleClose = () => {
    if (exchangeComplete && onSuccess) {
      onSuccess(exchangeResult);
    }
    onOpenChange(false);
  };

  const hasSelectedItems = returnItems.some(it => it.return_qty > 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-800">
            <ArrowLeftRight className="w-5 h-5 text-blue-500" />
            {exchangeComplete ? 'Exchange Complete' : 
              step === 1 ? 'Select Items to Exchange (Step 1/3)' : 
              step === 2 ? 'Add New Items (Step 2/3)' : 
              'Settlement (Step 3/3)'}
          </DialogTitle>
        </DialogHeader>

        {!exchangeComplete ? (
          <>
            {/* Progress Indicator */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {s}
                  </div>
                  {s < 3 && <div className={`w-12 h-1 ${step > s ? 'bg-blue-500' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>

            {/* Bill Reference */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-3">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">Original Bill</p>
                    <p className="font-bold text-gray-800">{bill?.bill_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Bill Amount</p>
                    <p className="font-bold text-gray-800">₹{bill?.grand_total?.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {step === 1 && (
              <>
                {/* Items Selection */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Product</TableHead>
                        <TableHead className="text-center">Purchased</TableHead>
                        <TableHead className="text-center">Available</TableHead>
                        <TableHead className="text-center">Exchange Qty</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {returnItems.map((item, idx) => (
                        <TableRow key={idx} className={item.return_qty > 0 ? 'bg-blue-50' : ''}>
                          <TableCell>
                            <p className="font-medium text-gray-800">{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.sku}</p>
                          </TableCell>
                          <TableCell className="text-center">{item.quantity}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={item.available_qty > 0 ? 'default' : 'secondary'}>
                              {item.available_qty}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => updateReturnQty(idx, item.return_qty - 1)}
                                disabled={item.return_qty <= 0}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <Input
                                type="number"
                                value={item.return_qty}
                                onChange={(e) => updateReturnQty(idx, parseInt(e.target.value) || 0)}
                                className="w-14 h-7 text-center"
                                min={0}
                                max={item.available_qty}
                              />
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => updateReturnQty(idx, item.return_qty + 1)}
                                disabled={item.return_qty >= item.available_qty}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={item.reason} 
                              onValueChange={(v) => updateReason(idx, v)}
                              disabled={item.return_qty <= 0}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="exchange">Exchange</SelectItem>
                                <SelectItem value="size_issue">Size Issue</SelectItem>
                                <SelectItem value="color_change">Color Change</SelectItem>
                                <SelectItem value="defective">Defective</SelectItem>
                                <SelectItem value="wrong_item">Wrong Item</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-red-600">
                            {item.return_qty > 0 ? `-₹${retLineValue(item).toFixed(2)}` : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Return Total */}
                {hasSelectedItems && (
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-3 flex justify-between items-center">
                      <span className="font-medium text-gray-700">Items to Return Value:</span>
                      <span className="text-xl font-bold text-red-600">-₹{totals.returnTotal.toFixed(2)}</span>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {step === 2 && (
              <>
                {/* Return Summary */}
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Returning:</p>
                    {returnItems.filter(it => it.return_qty > 0).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>{item.product_name} x {item.return_qty}</span>
                        <span className="text-red-600">-₹{retLineValue(item).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t mt-2 pt-2 flex justify-between font-semibold">
                      <span>Return Credit:</span>
                      <span className="text-red-600">-₹{totals.returnTotal.toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Product Search */}
                <div className="space-y-2">
                  <Label>Search Products to Add</Label>
                  <div className="flex gap-2">
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          searchProducts();
                        }
                      }}
                      placeholder="Search by name, SKU, or barcode... (Press Enter)"
                      className="bg-gray-50"
                      data-testid="exchange-product-search"
                    />
                    <Button onClick={searchProducts} disabled={searching}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {searchResults.map((product) => (
                      <div 
                        key={product.id}
                        className="p-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center border-b last:border-b-0"
                        onClick={() => addNewItem(product)}
                      >
                        <div>
                          <p className="font-medium text-sm">{product.name}</p>
                          <p className="text-xs text-gray-500">{product.sku}</p>
                        </div>
                        <p className="font-semibold text-emerald-600">
                          ₹{(product.current_price?.selling_price || product.selling_price || 0).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* New Items List */}
                <div className="space-y-2">
                  <Label>New Items ({newItems.length})</Label>
                  {newItems.length === 0 ? (
                    <Card className="bg-gray-50">
                      <CardContent className="p-6 text-center text-gray-500">
                        <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p>Search and add products for exchange</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="border rounded-lg">
                      {newItems.map((item, idx) => (
                        <div key={idx} className="p-2 flex items-center justify-between border-b last:border-b-0 bg-green-50">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.sku}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => updateNewItemQty(idx, item.quantity - 1)}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-8 text-center">{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => updateNewItemQty(idx, item.quantity + 1)}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                            <span className="w-32 text-right font-semibold text-emerald-600" data-testid={`exc-new-value-${item.sku}`}>
                              +₹{newLineValue(item).toFixed(2)}
                              {quotedNew(item.product_id)?.offer_name && <div className="text-[10px] font-normal text-indigo-600 truncate" data-testid={`exc-new-offer-${item.sku}`}>{quotedNew(item.product_id).offer_name} · −₹{quotedNew(item.product_id).discount_amount.toFixed(2)}</div>}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-500"
                              onClick={() => removeNewItem(idx)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Balance Preview */}
                {newItems.length > 0 && (
                  <Card className={totals.difference >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}>
                    <CardContent className="p-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm text-gray-600">New Items: +₹{totals.newTotal.toFixed(2)}</p>
                          <p className="text-sm text-gray-600">Return Credit: -₹{totals.returnTotal.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Balance</p>
                          <p className={`text-xl font-bold ${totals.difference >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                            {totals.difference >= 0 ? `+₹${totals.difference.toFixed(2)}` : `-₹${Math.abs(totals.difference).toFixed(2)}`}
                          </p>
                          <p className="text-xs text-gray-500">
                            {totals.difference > 0 ? 'Customer to Pay' : totals.difference < 0 ? 'Refund to Customer' : 'Even Exchange'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {step === 3 && (
              <>
                {/* Exchange Summary */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Return Items */}
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-3">
                      <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-1">
                        <ArrowRight className="w-4 h-4 text-red-500 rotate-180" />
                        Returning
                      </h4>
                      {returnItems.filter(it => it.return_qty > 0).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span>{item.product_name} x {item.return_qty}</span>
                          <span className="text-red-600">-₹{retLineValue(item).toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="border-t mt-2 pt-2 flex justify-between font-bold">
                        <span>Total:</span>
                        <span className="text-red-600">-₹{totals.returnTotal.toFixed(2)}</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* New Items */}
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-3">
                      <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-1">
                        <ArrowRight className="w-4 h-4 text-green-500" />
                        Receiving
                      </h4>
                      {newItems.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span>{item.product_name} x {item.quantity}{quotedNew(item.product_id)?.offer_name && <span className="ml-1 text-[10px] text-indigo-600">({quotedNew(item.product_id).offer_name})</span>}</span>
                          <span className="text-green-600">+₹{newLineValue(item).toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="border-t mt-2 pt-2 flex justify-between font-bold">
                        <span>Total:</span>
                        <span className="text-green-600">+₹{totals.newTotal.toFixed(2)}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Balance Card */}
                <Card className={`${totals.difference > 0 ? 'bg-emerald-100 border-emerald-300' : totals.difference < 0 ? 'bg-orange-100 border-orange-300' : 'bg-gray-100 border-gray-300'}`}>
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">
                      {totals.difference > 0 ? 'Amount to Collect from Customer' : 
                       totals.difference < 0 ? 'Amount to Refund to Customer' : 
                       'Even Exchange - No Payment Required'}
                    </p>
                    <p className={`text-3xl font-bold ${totals.difference > 0 ? 'text-emerald-600' : totals.difference < 0 ? 'text-orange-600' : 'text-gray-600'}`}>
                      ₹{Math.abs(totals.difference).toFixed(2)}
                    </p>
                  </CardContent>
                </Card>

                {/* Payment Mode Selection (only if there's a difference) */}
                {totals.difference !== 0 && (
                  <div className="space-y-3">
                    <Label className="text-gray-700">
                      {totals.difference > 0 ? 'Payment Mode' : 'Refund Mode'}
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      <Button
                        type="button"
                        variant={paymentMode === 'cash' ? 'default' : 'outline'}
                        onClick={() => setPaymentMode('cash')}
                        className={`h-16 flex-col gap-1 ${paymentMode === 'cash' ? 'bg-blue-500 hover:bg-blue-600' : ''}`}
                      >
                        <Banknote className="w-5 h-5" />
                        <span className="text-xs">Cash</span>
                      </Button>
                      <Button
                        type="button"
                        variant={paymentMode === 'card' ? 'default' : 'outline'}
                        onClick={() => setPaymentMode('card')}
                        className={`h-16 flex-col gap-1 ${paymentMode === 'card' ? 'bg-blue-500 hover:bg-blue-600' : ''}`}
                      >
                        <CreditCard className="w-5 h-5" />
                        <span className="text-xs">Card</span>
                      </Button>
                      <Button
                        type="button"
                        variant={paymentMode === 'upi' ? 'default' : 'outline'}
                        onClick={() => setPaymentMode('upi')}
                        className={`h-16 flex-col gap-1 ${paymentMode === 'upi' ? 'bg-blue-500 hover:bg-blue-600' : ''}`}
                      >
                        <Wallet className="w-5 h-5" />
                        <span className="text-xs">UPI</span>
                      </Button>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Notes (Required)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Exchange reason, customer details, etc."
                    className="bg-gray-50"
                    rows={2}
                  />
                </div>

                {/* Cash Register Impact Warning */}
                {paymentMode === 'cash' && totals.difference !== 0 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-semibold">Cash Register Impact</p>
                      <p>
                        {totals.difference > 0 
                          ? `₹${totals.difference.toFixed(2)} will be added to your cash register.`
                          : `₹${Math.abs(totals.difference).toFixed(2)} will be deducted from your cash register.`}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            <DialogFooter className="gap-2">
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)}>
                  Back
                </Button>
              )}
              {quoteError && <div className="w-full text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-1" data-testid="exc-quote-error">{quoteError}</div>}
              <Button variant="outline" onClick={handleClose} data-testid="exc-cancel">
                Cancel
              </Button>
              {step === 1 && (
                <Button
                  onClick={proceedToStep2}
                  disabled={!hasSelectedItems || !!quoteError || quoting}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="exc-next-1"
                >
                  Next: Add Items →
                </Button>
              )}
              {step === 2 && (
                <Button
                  onClick={proceedToStep3}
                  disabled={newItems.length === 0 || !!quoteError || quoting}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="exc-next-2"
                >
                  Next: Settlement →
                </Button>
              )}
              {step === 3 && (
                <Button
                  onClick={processExchange}
                  disabled={processing || !notes.trim() || !!quoteError || quoting || !quote}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="exc-process"
                >
                  {processing ? 'Processing...' : 
                    totals.difference > 0 ? `Collect ₹${totals.difference.toFixed(2)} & Complete` :
                    totals.difference < 0 ? `Refund ₹${Math.abs(totals.difference).toFixed(2)} & Complete` :
                    'Complete Exchange'}
                </Button>
              )}
            </DialogFooter>
          </>
        ) : (
          <>
            {/* Exchange Complete */}
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">Exchange Completed Successfully</h2>
              <p className="text-gray-600 mb-4">Exchange #{exchangeResult?.exchange_number}</p>
              
              <Card className="bg-green-50 border-green-200 mb-4">
                <CardContent className="p-4">
                  {exchangeResult?.price_difference > 0 ? (
                    <div>
                      <p className="text-sm text-gray-600">Amount Collected</p>
                      <div className="text-2xl font-bold text-emerald-600">
                        +₹{exchangeResult?.price_difference?.toFixed(2)}
                      </div>
                    </div>
                  ) : exchangeResult?.price_difference < 0 ? (
                    <div>
                      <p className="text-sm text-gray-600">Amount Refunded</p>
                      <div className="text-2xl font-bold text-orange-600" data-testid="exc-refund-amount">
                        ₹{Math.abs(exchangeResult?.price_difference)?.toFixed(2)}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600">Even Exchange</p>
                      <div className="text-xl font-bold text-gray-600">No Payment</div>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 mt-1">via {paymentMode}</p>
                </CardContent>
              </Card>

              {/* Exchange Receipt - Thermal Printer Optimized */}
              <div className="hidden">
                <div ref={receiptRef} className="exchange-receipt-thermal">
                  {/* Header */}
                  <div className="receipt-header">
                    <div className="receipt-title">EXCHANGE RECEIPT</div>
                    <div className="store-name">{bill?.store_name || 'SOON YOUNG'}</div>
                  </div>

                  {/* Receipt Info */}
                  <div className="info-section">
                    <div className="info-row">
                      <span>Exchange #:</span>
                      <span className="bold">{exchangeResult?.exchange_number}</span>
                    </div>
                    <div className="info-row">
                      <span>Original Bill:</span>
                      <span>{bill?.bill_number}</span>
                    </div>
                    <div className="info-row">
                      <span>Date:</span>
                      <span>{new Date().toLocaleDateString('en-IN')} {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>

                  {/* Returned Items */}
                  <div className="items-section">
                    <div className="section-title">RETURNED ITEMS</div>
                    {returnItems.filter(it => it.return_qty > 0).map((item, idx) => (
                      <div key={idx} className="item-row">
                        <div className="item-name">{item.product_name}</div>
                        <div className="item-details">
                          <span className="col-qty">x {item.return_qty}</span>
                          <span className="col-rate">@ Rs.{(quotedRet(item.product_id)?.paid_unit_price ?? item.unit_price).toFixed(2)}</span>
                          <span className="col-amt return-amt">-Rs.{retLineValue(item).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                    <div className="subtotal-row">
                      <span>Return Total:</span>
                      <span className="return-amt">-Rs.{totals.returnTotal.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* New Items */}
                  <div className="items-section">
                    <div className="section-title">NEW ITEMS</div>
                    {newItems.map((item, idx) => (
                      <div key={idx} className="item-row">
                        <div className="item-name">{item.product_name}</div>
                        <div className="item-details">
                          <span className="col-qty">x {item.quantity}</span>
                          <span className="col-rate">@ Rs.{(quotedNew(item.product_id)?.effective_unit_price ?? item.unit_price).toFixed(2)}{quotedNew(item.product_id)?.offer_name ? ` (${quotedNew(item.product_id).offer_name})` : ''}</span>
                          <span className="col-amt new-amt">+Rs.{newLineValue(item).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                    <div className="subtotal-row">
                      <span>New Total:</span>
                      <span className="new-amt">+Rs.{totals.newTotal.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Settlement Box */}
                  <div className="settlement-box">
                    <div className="settlement-label">
                      {exchangeResult?.price_difference >= 0 ? 'COLLECTED FROM CUSTOMER' : 'REFUNDED TO CUSTOMER'}
                    </div>
                    <div className="settlement-amount">Rs. {Math.abs(exchangeResult?.price_difference || 0).toFixed(2)}</div>
                  </div>

                  {/* Payment Mode */}
                  {exchangeResult?.price_difference !== 0 && (
                    <div className="payment-mode">
                      <span>Payment Mode:</span>
                      <span className="bold">{paymentMode.toUpperCase()}</span>
                    </div>
                  )}

                  {/* Notes */}
                  {notes && (
                    <div className="notes-section">
                      <span className="bold">Notes:</span> {notes}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="receipt-footer">
                    <div className="thank-you">Thank You!</div>
                    <div className="footer-note">Computer Generated Receipt</div>
                  </div>

                  {/* Thermal Print Styles - ALL BOLD */}
                  <style>{`
                    .exchange-receipt-thermal {
                      width: 280px;
                      padding: 12px;
                      font-family: 'Arial', 'Helvetica', sans-serif;
                      font-size: 12px;
                      line-height: 1.4;
                      background: white;
                      color: black;
                      font-weight: 700;
                    }
                    .exchange-receipt-thermal .receipt-header {
                      text-align: center;
                      border-bottom: 2px solid black;
                      padding-bottom: 8px;
                      margin-bottom: 8px;
                    }
                    .exchange-receipt-thermal .receipt-title {
                      font-size: 16px;
                      font-weight: 900;
                      letter-spacing: 1px;
                    }
                    .exchange-receipt-thermal .store-name {
                      font-size: 12px;
                      font-weight: 800;
                      margin-top: 4px;
                    }
                    .exchange-receipt-thermal .info-section {
                      border-bottom: 1px dashed black;
                      padding-bottom: 8px;
                      margin-bottom: 8px;
                    }
                    .exchange-receipt-thermal .info-row {
                      display: flex;
                      justify-content: space-between;
                      font-size: 11px;
                      font-weight: 700;
                      margin-bottom: 2px;
                    }
                    .exchange-receipt-thermal .bold { font-weight: 900; }
                    .exchange-receipt-thermal .items-section {
                      border-bottom: 2px solid black;
                      padding-bottom: 8px;
                      margin-bottom: 8px;
                    }
                    .exchange-receipt-thermal .section-title {
                      font-size: 11px;
                      font-weight: 900;
                      margin-bottom: 6px;
                      border-bottom: 1px solid black;
                      padding-bottom: 2px;
                    }
                    .exchange-receipt-thermal .item-row {
                      margin-bottom: 6px;
                      padding-bottom: 4px;
                      border-bottom: 1px dotted #666;
                    }
                    .exchange-receipt-thermal .item-row:last-of-type { border-bottom: none; }
                    .exchange-receipt-thermal .item-name {
                      font-size: 12px;
                      font-weight: 900;
                      margin-bottom: 2px;
                    }
                    .exchange-receipt-thermal .item-details {
                      display: flex;
                      font-size: 11px;
                      font-weight: 700;
                    }
                    .exchange-receipt-thermal .item-details .col-qty { width: 50px; }
                    .exchange-receipt-thermal .item-details .col-rate { flex: 1; }
                    .exchange-receipt-thermal .item-details .col-amt { width: 80px; text-align: right; font-weight: 900; }
                    .exchange-receipt-thermal .return-amt { font-weight: 900; }
                    .exchange-receipt-thermal .new-amt { font-weight: 900; }
                    .exchange-receipt-thermal .subtotal-row {
                      display: flex;
                      justify-content: space-between;
                      font-size: 11px;
                      font-weight: 900;
                      margin-top: 6px;
                      padding-top: 4px;
                      border-top: 1px dotted black;
                    }
                    .exchange-receipt-thermal .settlement-box {
                      border: 2px solid black;
                      padding: 8px;
                      text-align: center;
                      margin-bottom: 8px;
                    }
                    .exchange-receipt-thermal .settlement-label {
                      font-size: 11px;
                      font-weight: 900;
                    }
                    .exchange-receipt-thermal .settlement-amount {
                      font-size: 18px;
                      font-weight: 900;
                    }
                    .exchange-receipt-thermal .payment-mode {
                      display: flex;
                      justify-content: space-between;
                      font-size: 11px;
                      font-weight: 700;
                      border-bottom: 1px dashed black;
                      padding-bottom: 8px;
                      margin-bottom: 8px;
                    }
                    .exchange-receipt-thermal .notes-section {
                      font-size: 11px;
                      font-weight: 700;
                      border-bottom: 1px dashed black;
                      padding-bottom: 8px;
                      margin-bottom: 8px;
                    }
                    .exchange-receipt-thermal .receipt-footer {
                      text-align: center;
                      margin-top: 8px;
                    }
                    .exchange-receipt-thermal .thank-you {
                      font-size: 14px;
                      font-weight: 900;
                    }
                    .exchange-receipt-thermal .footer-note {
                      font-size: 10px;
                      font-weight: 700;
                      margin-top: 4px;
                    }
                    @media print {
                      .exchange-receipt-thermal {
                        width: 70mm !important;
                        padding: 0 !important;
                        padding-left: 3mm !important;
                        padding-right: 2mm !important;
                        padding-top: 2mm !important;
                        padding-bottom: 2mm !important;
                        margin: 0 !important;
                        font-weight: 700 !important;
                      }
                      .exchange-receipt-thermal * {
                        color: black !important;
                        background: transparent !important;
                        font-weight: 700 !important;
                      }
                      .exchange-receipt-thermal .receipt-title,
                      .exchange-receipt-thermal .item-name,
                      .exchange-receipt-thermal .section-title,
                      .exchange-receipt-thermal .settlement-label,
                      .exchange-receipt-thermal .settlement-amount,
                      .exchange-receipt-thermal .subtotal-row,
                      .exchange-receipt-thermal .col-amt,
                      .exchange-receipt-thermal .thank-you {
                        font-weight: 900 !important;
                      }
                      @page {
                        size: 80mm auto;
                        margin: 0;
                      }
                    }
                  `}</style>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print Receipt
              </Button>
              <Button onClick={handleClose} className="bg-emerald-500 hover:bg-emerald-600">
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExchangeDialog;
